import type { AddressType } from '@sweetgreen/garnish';
import { AddressTypes } from '@sweetgreen/garnish';

import type { OrderStatus, OrderType, Restaurant } from '@order/graphql';
import { FlattenedOrderStatuses } from '@order/graphql';
import type { TranslationValues } from '@order/Localization';
import { useLocalizationContext } from '@order/Localization';
import {
  getDerivedOrderStatuses,
  getOrderTimeRanges,
} from '@order/OrderStatus';

import {
  getDay,
  ignoreTimezone,
  isDayAfterTomorrow,
  isTomorrow,
} from '../../../../utils';
import { getGreetingMode } from './getGreetingMode';
import type { GetOrderStatusQuery } from './OrderStatus.query.generated';

export const DEFAULT_PARAMS = {
  orderType: AddressTypes.pickup,
  orderStatus: FlattenedOrderStatuses.Received,
  canTrackOrderStatus: false,
  isPendingFeedback: false,
  hasCart: false,
  showSeasonalMessaging: true,
  cartOrderType: AddressTypes.pickup,
  wantedTime: '',
  estimatedDeliveryTime: '',
  restaurantName: '',
  dropoffLocation: '',
  customerName: '',
  deliveryAddressName: '',
  orderId: '',
};

export const DEFAULT_TRANSLATIONS: TranslationValues = {
  greetingMode: 'signedOut',
  orderStatusWithExtraTypes: 'pickup',
  orderStatusWithoutLateOrder: 'pickup',
  dayOfWeek: '',
  wantedTimeTs: new Date(),
  monthDayTs: new Date(),
  timeRangeStartTs: new Date(),
  timeRangeEndTs: new Date(),
  ...DEFAULT_PARAMS,
};

type HookProps = {
  readonly isLoggedIn: boolean;
  readonly isLoading: boolean;
  readonly hasCart: boolean;
  readonly cartOrderType: AddressType;
  readonly data?: GetOrderStatusQuery;
  readonly orderPendingFeedbackId?: string;
};

type ReturnProps = {
  readonly i18nValues: typeof DEFAULT_TRANSLATIONS;
  readonly params: typeof DEFAULT_PARAMS;
};

export const useI18nValues = (props: HookProps): ReturnProps => {
  const {
    isLoggedIn,
    data,
    hasCart,
    cartOrderType,
    isLoading,
    orderPendingFeedbackId,
  } = props;
  const { t } = useLocalizationContext();

  // ──── Loading Orders ─────────────────────────────────────────────────
  if (isLoading || !isLoggedIn || !data?.currentCustomer?.firstName) {
    return { i18nValues: DEFAULT_TRANSLATIONS, params: DEFAULT_PARAMS };
  }

  // ──── Context Data ───────────────────────────────────────────────────
  const nonOrderStatusParams = {
    customerName: data.currentCustomer.firstName,
    hasCart,
    cartOrderType,
  };

  // ──── Query Data ─────────────────────────────────────────────────────
  const orderStatusResponse = (data.orderStatus ?? {}) as OrderStatus;
  const orderId = orderStatusResponse?.order?.id;
  const { order, flattenedOrderStatus } = orderStatusResponse;
  const { isPast } = getDerivedOrderStatuses({
    wantedTime: order?.wantedTime,
    flattenedOrderStatus,
  });

  // ──── Rate Order ─────────────────────────────────────────────────────
  if (
    orderPendingFeedbackId &&
    !hasHighPriorityStatus(flattenedOrderStatus, hasCart, isPast)
  ) {
    return {
      i18nValues: {
        ...DEFAULT_TRANSLATIONS,
        greetingMode: getGreetingMode({ isLoggedIn }),
        customerName: data.currentCustomer.firstName,
        orderStatusWithExtraTypes: 'feedback',
        orderStatusWithoutLateOrder: 'feedback',
      },
      params: {
        ...DEFAULT_PARAMS,
        ...nonOrderStatusParams,
        showSeasonalMessaging: false,
        isPendingFeedback: true,
        orderId: orderPendingFeedbackId,
      },
    };
  }

  // ──── New Customer ───────────────────────────────────────────────────
  if (!data.orderStatus) {
    return {
      i18nValues: {
        ...DEFAULT_TRANSLATIONS,
        greetingMode: 'newCustomer',
        customerName: data.currentCustomer.firstName,
      },
      params: {
        ...DEFAULT_PARAMS,
        ...nonOrderStatusParams,
      },
    };
  }

  // ──── Params ─────────────────────────────────────────────────────────
  const params: typeof DEFAULT_PARAMS = {
    orderType: convertOrderType(order.orderType, order.restaurant),
    orderStatus: flattenedOrderStatus,
    canTrackOrderStatus: order.canTrackOrderStatus,
    isPendingFeedback: order.isPendingFeedback,
    restaurantName: order.restaurant.name,
    dropoffLocation: order.dropoffLocation ?? '',
    wantedTime: order.wantedTime,
    estimatedDeliveryTime:
      order.deliveryOrderDetail?.estimatedDeliveryTime ?? '',
    deliveryAddressName: order.deliveryOrderDetail?.address.name ?? '',
    showSeasonalMessaging: isPast && !order.isPendingFeedback,
    orderId,
    ...nonOrderStatusParams,
  };

  // ──── Time Range ─────────────────────────────────────────────────────
  const normalizedWantedTime = params.wantedTime
    ? ignoreTimezone(params.wantedTime)
    : undefined;
  const normalizedEstimatedDeliveryTime = params.estimatedDeliveryTime
    ? ignoreTimezone(params.estimatedDeliveryTime)
    : undefined;

  const {
    wantedTimeStart,
    wantedTimeEnd,
    estimatedDeliveryTimeStart,
    estimatedDeliveryTimeEnd,
  } = getOrderTimeRanges({
    wantedTime: normalizedWantedTime,
    estimatedDeliveryTime: normalizedEstimatedDeliveryTime,
  });
  const timeRangeStartTs = estimatedDeliveryTimeStart ?? wantedTimeStart;
  const timeRangeEndTs = estimatedDeliveryTimeEnd ?? wantedTimeEnd;
  const restaurantWantedTime = normalizedWantedTime ?? '';

  // ──── Greeting Mode ──────────────────────────────────────────────────
  // Having an active order means having a previous order + one of the following two conditions:
  // - Last order was completed less than three hours ago.
  // - Has a cart.
  const hasActiveOrder = !isPast || params.hasCart;
  const greetingMode = getGreetingMode({ isLoggedIn, hasActiveOrder });

  // ──── Localization ───────────────────────────────────────────────────
  const i18nValues: typeof DEFAULT_TRANSLATIONS = {
    ...params,
    orderStatusWithExtraTypes: addSpecialOrderStatusesForTranslation({
      params,
      hasCart,
    }),
    orderStatusWithoutLateOrder: addSpecialOrderStatusesForTranslation({
      params,
      headline: true,
      hasCart,
    }),
    greetingMode,
    timeEstimate: isTomorrow(restaurantWantedTime)
      ? 'tomorrow'
      : isDayAfterTomorrow(restaurantWantedTime)
      ? 'afterTomorrow'
      : 'today',
    dayOfWeek: restaurantWantedTime
      ? t(getDay(`${restaurantWantedTime.getDay()}`))
      : '-',
    wantedTimeTs: restaurantWantedTime,
    monthDayTs: restaurantWantedTime,
    timeRangeStartTs,
    timeRangeEndTs,
  };

  return {
    i18nValues,
    params,
  };
};

// To be replaced with monograph order type.
const convertOrderType = (
  orderType: OrderType,
  restaurant?: Partial<Restaurant>,
): AddressType => {
  if (restaurant?.isOutpost) return 'outpost';

  return orderType.toLowerCase() as AddressType;
};

// For the purposes of the home screen translation,
// we need to return the following "order statuses":
// feedback - noTrackingData - lateOrder
const addSpecialOrderStatusesForTranslation = ({
  params,
  headline,
  hasCart,
}: {
  readonly params: typeof DEFAULT_PARAMS;
  readonly headline?: boolean;
  readonly hasCart?: boolean;
}) => {
  const { isLate, isPast, isPendingFeedback, isTrackable } =
    getDerivedOrderStatuses({
      order: {
        isPendingFeedback: params.isPendingFeedback,
        canTrackOrderStatus: params.canTrackOrderStatus,
      },
      wantedTime: params.wantedTime,
      flattenedOrderStatus: params.orderStatus,
    });
  const mustBeTrackable = ['completed', 'failed'].includes(params.orderStatus);

  if (hasCart) return 'pending';

  if (isLate && !headline) return 'lateOrder';

  if (isPendingFeedback && isPast) return 'feedback';

  if (!mustBeTrackable && !isTrackable) return 'noTrackingData';

  return params.orderStatus;
};

// We consider ongoing orders as high priority, these will be shown before rate order requests.
const hasHighPriorityStatus = (
  flattenedOrderStatus: string,
  hasCart: boolean,
  isPast: boolean,
) => {
  const isOngoingStatus = [
    'received',
    'preparing',
    'readyForCourier',
    'delivering',
  ].includes(flattenedOrderStatus);

  const isRecentlyCompleted = flattenedOrderStatus === 'completed' && !isPast;

  // These flags represent an ongoing order.
  return hasCart || isOngoingStatus || isRecentlyCompleted;
};
